import { useState } from "react";
import {
  CreditCard,
  Download,
  FileText,
  Inbox,
  KeyRound,
  Loader2,
  MailSearch,
  RefreshCw,
  Trash2,
  Upload,
  AlertTriangle,
  Landmark,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtRp, fmtRpShort } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { KpiCard, SectionCard } from "@/components/common/Cards";
import { STATUS_META, cardName, downloadBlob, groupBy } from "@/components/cc/ccUtils";

export function StatementsTab({ statements, loading, job, pulling, onPull, onUpload, onOpen, onReload }) {
  const [pw, setPw] = useState({});
  const [busy, setBusy] = useState({});
  const [del, setDel] = useState(null);

  const list = statements || [];
  const ready = list.filter((s) => s.status === "ready");
  const totalDue = ready.reduce((a, s) => a + (s.total_due || 0), 0);
  const charges = ready.reduce((a, s) => a + (s.total_charges || 0), 0);
  const banks = groupBy(
    ready.map((s) => ({ bank: s.bank || "Lainnya", amount: s.total_charges || 0 })),
    "bank"
  );

  const reanalyze = async (s, password) => {
    setBusy((b) => ({ ...b, [s.id]: true }));
    try {
      await api.post(`/cc/statements/${s.id}/reanalyze`, password ? { password } : {});
      toast.success("PDF terbuka, AI sedang merangkum…");
      setPw((p) => ({ ...p, [s.id]: "" }));
      onReload();
    } catch (e) {
      toast.error(errMsg(e, "Gagal memproses"));
    } finally {
      setBusy((b) => ({ ...b, [s.id]: false }));
    }
  };
  const remove = async () => {
    const s = del;
    setDel(null);
    try {
      await api.delete(`/cc/statements/${s.id}`);
      toast.success("Tagihan dihapus");
      onReload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2" data-testid="cc-statements-toolbar">
        <Button onClick={onPull} disabled={pulling} className="press h-11 px-5" data-testid="cc-pull-gmail">
          {pulling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MailSearch className="h-4 w-4 mr-2" />} Tarik dari Gmail
        </Button>
        <Button variant="outline" onClick={onUpload} className="h-11 px-5" data-testid="cc-open-upload">
          <Upload className="h-4 w-4 mr-2" /> Upload PDF
        </Button>
        <Button variant="ghost" onClick={onReload} className="h-11" data-testid="cc-reload">
          <RefreshCw className="h-4 w-4 mr-2" /> Muat ulang
        </Button>
        {job && (
          <span
            className={`text-xs ml-auto max-w-[520px] ${job.status === "error" ? "text-[hsl(0_72%_66%)]" : "text-muted-foreground"}`}
            data-testid="cc-job-message"
          >
            {job.status === "running" && <Loader2 className="inline h-3 w-3 mr-1 animate-spin" />}
            {job.message}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Tagihan diimpor" value={list.length} sub={`${ready.length} siap · ${list.length - ready.length} lainnya`} icon={Inbox} testid="cc-kpi-count" />
        <KpiCard label="Total tagihan" value={fmtRp(totalDue)} accent icon={CreditCard} testid="cc-kpi-total-due" />
        <KpiCard label="Total transaksi" value={fmtRp(charges)} sub="Belanja + biaya, sebelum kredit" testid="cc-kpi-charges" />
        <KpiCard label="Provider kartu" value={banks.length} sub={banks.slice(0, 3).map((b) => b.name).join(", ") || "–"} icon={Landmark} testid="cc-kpi-banks" />
      </div>

      {loading && !statements ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-48 rounded-xl bg-[hsl(220_10%_11%)]" />)}
        </div>
      ) : list.length === 0 ? (
        <SectionCard testid="cc-empty">
          <div className="py-8 flex flex-col items-center text-center">
            <CreditCard className="h-8 w-8 text-primary" />
            <h3 className="font-display text-2xl mt-3">Belum ada tagihan</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md">
              Tarik e-statement langsung dari Gmail (atur di tab Pengaturan Gmail) atau unggah beberapa PDF tagihan sekaligus.
            </p>
            <Button onClick={onUpload} className="mt-5 press" data-testid="cc-empty-upload"><Upload className="h-4 w-4 mr-2" /> Upload PDF tagihan</Button>
          </div>
        </SectionCard>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="cc-statement-list">
          {list.map((s) => {
            const st = STATUS_META[s.status] || STATUS_META.error;
            const cats = groupBy((s.transactions || []).filter((t) => t.amount > 0), "category").slice(0, 3);
            return (
              <div key={s.id} className="rounded-xl border border-border bg-card p-4 elev-1 flex flex-col" data-testid={`cc-statement-${s.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg border border-[color:var(--hairline)] bg-[hsl(43_30%_9%)] flex items-center justify-center shrink-0">
                    <CreditCard className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate" title={cardName(s)}>{cardName(s)}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {[s.period, s.source === "gmail" ? "Gmail" : "Upload", s.filename].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${st.cls}`} data-testid={`cc-statement-status-${s.id}`}>
                    {s.status === "processing" && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                    {st.label}
                  </Badge>
                </div>

                {s.status === "ready" && (
                  <>
                    <div className="mt-4 flex items-end justify-between gap-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Total tagihan</div>
                        <div className="num text-xl mt-0.5">{fmtRp(s.total_due)}</div>
                      </div>
                      <div className="text-right text-[11px] text-muted-foreground">
                        <div className="num">{(s.transactions || []).length} transaksi</div>
                        {s.due_date && <div>Jatuh tempo {s.due_date}</div>}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {cats.map((c) => (
                        <Badge key={c.name} variant="outline" className="border-border text-[10px] text-[hsl(45_15%_80%)]">
                          {c.name} · {fmtRpShort(c.total)}
                        </Badge>
                      ))}
                    </div>
                  </>
                )}

                {s.status === "error" && (
                  <div className="mt-3 rounded-lg border border-[hsl(0_45%_28%)] bg-[hsl(0_40%_7%)] p-3" data-testid={`cc-statement-error-${s.id}`}>
                    <div className="flex gap-2 text-xs text-[hsl(0_70%_72%)]">
                      <AlertTriangle className="h-4 w-4 shrink-0" /> <span>{s.error}</span>
                    </div>
                    {s.needs_password && (
                      <div className="flex gap-2 mt-2.5">
                        <Input
                          type="password"
                          value={pw[s.id] || ""}
                          onChange={(e) => setPw((p) => ({ ...p, [s.id]: e.target.value }))}
                          onKeyDown={(e) => e.key === "Enter" && pw[s.id] && reanalyze(s, pw[s.id])}
                          placeholder="Password PDF"
                          className="h-9 bg-[hsl(220_12%_6%)]"
                          autoComplete="off"
                          data-testid={`cc-unlock-input-${s.id}`}
                        />
                        <Button onClick={() => reanalyze(s, pw[s.id])} disabled={!pw[s.id] || busy[s.id]} className="h-9" data-testid={`cc-unlock-btn-${s.id}`}>
                          {busy[s.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1.5" />} Buka
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {s.status === "processing" && (
                  <p className="text-xs text-muted-foreground mt-4">AI sedang membaca transaksi dan mengelompokkan vendor & kategori. Biasanya 20–60 detik.</p>
                )}

                <div className="flex flex-wrap gap-1.5 mt-auto pt-4">
                  <Button size="sm" variant="outline" disabled={s.status !== "ready"} onClick={() => onOpen(s)} data-testid={`cc-statement-open-${s.id}`}>
                    <FileText className="h-3.5 w-3.5 mr-1.5" /> Detail
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => downloadBlob(`/cc/statements/${s.id}/file`, s.filename, { inline: true }).catch((e) => toast.error(errMsg(e)))} data-testid={`cc-statement-file-${s.id}`}>
                    <Download className="h-3.5 w-3.5 mr-1.5" /> PDF asli
                  </Button>
                  {!s.needs_password && s.status !== "processing" && (
                    <Button size="sm" variant="ghost" disabled={busy[s.id]} onClick={() => reanalyze(s)} data-testid={`cc-statement-reanalyze-${s.id}`}>
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Analisa ulang
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="ml-auto text-muted-foreground hover:text-[hsl(0_72%_66%)]" onClick={() => setDel(s)} data-testid={`cc-statement-delete-${s.id}`} aria-label="Hapus tagihan">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus tagihan ini?</AlertDialogTitle>
            <AlertDialogDescription>
              {del && cardName(del)} beserta file PDF aslinya akan dihapus. Laporan reimburse yang sudah dibuat tidak ikut terhapus.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cc-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="cc-delete-confirm">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
